import type { ReactNode } from 'react';
import { IonCard, IonCardContent, IonText } from '@ionic/react';
import { cn } from '../../../lib/utils';

interface TimelineCardProps {
  createdAt: string;
  lastReadAt?: string | null;
  highlighted?: boolean;
  /**
   * Optional header content (e.g. an author name) shown on the first row,
   * inline with the time. When provided, the card lays out as three stacked
   * rows: header + time, then full-width content, then actions. When omitted,
   * the content and time share a two-column row instead.
   */
  header?: ReactNode;
  children: ReactNode;
  /**
   * Optional actions (e.g. edit/delete buttons) rendered full-width below the
   * content, so they align with the card's right edge (under the time).
   */
  actions?: ReactNode;
}

const formatTime = (dateTime: string) => {
  const date = new Date(dateTime);
  return date.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Madrid',
  });
};

const UnreadDot = () => <div className="h-2 w-2 rounded-full bg-blue-600" />;

export const TimelineCard = ({
  createdAt,
  lastReadAt,
  highlighted = false,
  header,
  children,
  actions,
}: TimelineCardProps) => {
  const isUnread = !lastReadAt || new Date(createdAt) > new Date(lastReadAt);

  const time = (
    <IonText color="medium" className="flex-shrink-0 text-xs whitespace-nowrap">
      {formatTime(createdAt)}
    </IonText>
  );

  return (
    <IonCard className={cn('m-0 mb-3', highlighted ? 'bg-blue-100' : 'bg-white')}>
      <IonCardContent className="py-3">
        {header !== undefined ? (
          <>
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                {isUnread && (
                  <div className="flex-shrink-0">
                    <UnreadDot />
                  </div>
                )}
                {header}
              </div>
              {time}
            </div>
            <div className="mt-2 text-gray-700">{children}</div>
          </>
        ) : (
          <div className="flex items-start justify-between">
            {isUnread && (
              <div className="mr-2 mt-1 flex-shrink-0">
                <UnreadDot />
              </div>
            )}
            <div className="flex-1 text-gray-700">{children}</div>
            <div className="ml-3">{time}</div>
          </div>
        )}
        {actions}
      </IonCardContent>
    </IonCard>
  );
};
